import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import Loader from '@repo/ui/loader'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getBlockedPlaceTripOptions } from '../../../../api/blocked-places/get-blocked-place-trip-options'
import { getCenter } from '../../../../api/centers/get-center'
import { BlockedPlaceInput } from '../../../../lib/types/models/blocked-place/blocked-place-input'
import { CenterProperties } from '../../../../lib/types/models/center/properties.center'
import { TripProperties } from '../../../../lib/types/models/trip/properties.trip'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { createNumberSchema } from '../../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledRadioInput from '../../../elements/inputs/controlled-radio-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../../elements/validation-buttons'

type Props = BaseFormProps<BlockedPlaceInput> & {
  centerId: number
  className?: string
}

const formSchema: ZodType<BlockedPlaceInput> = z.object({
  journeyId: createNumberSchema('séjour'),
  firstName: createStringSchema('prénom', { nullish: true }),
  lastName: createStringSchema('NOM'),
  mainTripId: createNumberSchema('voyage principal'),
  secondaryTripId: createNumberSchema('voyage secondaire', { nullish: true })
})

export const BlockedPlaceForm = ({
  centerId,
  defaultValues,
  className,
  ...validationButtonProps
}: Props) => {
  const form = useForm<BlockedPlaceInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { data } = useQuery({
    queryKey: ['centers', centerId],
    queryFn: () => getCenter(centerId)
  })

  const journeyId = form.watch('journeyId')
  const mainTripId = form.watch('mainTripId')

  const { data: tripOptions } = useQuery({
    queryKey: ['centers', centerId, 'blocked-places', 'trip-options', journeyId],
    queryFn: () => getBlockedPlaceTripOptions(centerId, journeyId),
    enabled: !!journeyId
  })

  const mainTrips = tripOptions?.mainTrips
  const secondaryTrips = mainTrips?.find((trip) => trip.id === mainTripId)?.secondaryTrips

  if (!data) return <Loader center />

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex flex-col gap-3', className)}>
          <div className="flex flex-col gap-2">
            <Typography variant="tag" color="grey-lighter">
              Sélectionner un séjour :
            </Typography>
            {CenterProperties.getCenterJourneyInformation(data).map(
              ({ journeyId, period, title }) => (
                <FormField
                  key={journeyId}
                  control={form.control}
                  name="journeyId"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <div className="bg-white-hover px-3 py-2 rounded border border-grey-light">
                          <ControlledRadioInput
                            {...field}
                            required
                            label={title}
                            labelClassName="text-xs font-semibold"
                            labelStyle={{ color: period?.color }}
                            checked={field.value === journeyId}
                            onCheck={() => {
                              field.onChange(journeyId)
                              form.resetField('mainTripId', { defaultValue: undefined })
                              form.resetField('secondaryTripId', { defaultValue: undefined })
                            }}
                          />
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                />
              )
            )}
          </div>

          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledInput {...field} required type="text" label="NOM" />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledInput {...field} type="text" label="Prénom" />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="mainTripId"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    required
                    searchable
                    dropDownPosition="above"
                    disabled={!journeyId}
                    label={'Voyage principal'}
                    onChange={(value) => {
                      field.onChange(value)
                      form.resetField('secondaryTripId', { defaultValue: undefined })
                    }}
                    options={mainTrips?.map((trip) => ({
                      label: TripProperties.getTripLabel(trip),
                      value: trip.id
                    }))}
                    selectedOption={conditionalRender(!!field?.value, {
                      label: TripProperties.getTripLabel(
                        mainTrips?.find((trip) => trip.id === field.value)
                      ),
                      value: field.value
                    })}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="secondaryTripId"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    searchable
                    disabled={!mainTripId || !secondaryTrips?.length}
                    label={'Voyage secondaire'}
                    dropDownPosition="above"
                    options={secondaryTrips?.map((trip) => ({
                      label: TripProperties.getTripLabel(trip),
                      value: trip.id
                    }))}
                    selectedOption={conditionalRender(!!field?.value, {
                      label: TripProperties.getTripLabel(
                        secondaryTrips?.find((trip) => trip.id === field.value)
                      ),
                      value: field.value
                    })}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}
